/**
 * Enchainement lineaire des scenes du chapitre. Ne connait ni le DOM ni
 * `three` : il expose un etat a afficher et recoit des choix (voir ADR 0011).
 * Ne charge pas les fichiers de dialogue lui-meme, il expose `dialogueId` et
 * laisse l'appelant fournir le DialogueFile (registre dans src/data/dialogues).
 * Ne charge pas non plus les cartes d'exploration : il expose `mapId` et
 * laisse l'appelant (`chapter.ts`) resoudre la `MapDef` (registre dans
 * src/data/maps) -- meme principe, une seule fois.
 */

import type { Condition } from './types';
import type { NarrativeContext } from './dialogueRunner';
import type { RunState } from './runState';
import { setFlag } from './runState';
import type { CharacterId } from '@/rules/character';
import { evaluateCondition } from './conditions';
import type { ObjectiveDef } from './objective';

/**
 * `explore` est une scene speciale (ADR 0013 §4, epic 3 lot 3.6b) : au lieu
 * d'un dialogue, le joueur se deplace sur une carte (`src/explore`) jusqu'a
 * declencher l'entite `objective.completionTrigger` -- voir `SceneDef` pour
 * le detail des champs propres a ce type.
 */
export type SceneKind = 'dialogue' | 'tactical' | 'explore' | 'debrief';

/**
 * Suiveur d'exploration (ADR 0024 §3) : un cadet du roster, ou l'enfant recueilli au
 * chapitre 2 (pas de `CharacterSheet`, silhouette dediee -- voir `CHILD_VISUAL_PROFILE`).
 */
export type FollowerId = CharacterId | 'enfant';

/**
 * Nombre de suiveurs effectivement RENDUS derriere Franklyn (08-EXPLORATION.md "Le groupe").
 * Reglable en donnees plutot qu'en dur dans `chapter.ts` : le chapitre 2 peut en declarer
 * jusqu'a cinq (ADR 0024, mesure au lot 5.7).
 *
 * **Decision du propriétaire (2026-09-26, B9)** : deux suiveurs visibles, le reste dit par la
 * narration -- malgre une marge mesuree confortable jusqu'a cinq (149 appels de dessin contre
 * un seuil de 250, TECH-DESIGN §6 "Lot 5.7 -- mesure"). Passee ici a 2, PAS specifique au
 * chapitre 2 : sans effet sur le chapitre 1, dont `exploreFollowerIds` ne renvoie jamais plus
 * de deux coequipiers (l'equipe bleue moins Franklyn, toujours 2 sur un roster de 3) -- une
 * limite chapitre-par-chapitre n'aurait donc rien changé pour lui et aurait ajoute une
 * indirection inutile. Le chapitre 2 (lot 5.8, `ch2.fuite`) est le premier a en ressentir
 * l'effet reel : `SceneDef.followers` en declare jusqu'a cinq, seuls les deux premiers sont
 * rendus -- c'est donc l'ORDRE de ce tableau qui choisit qui on voit (voir sa doc).
 */
export const VISIBLE_FOLLOWERS_LIMIT = 2;

export interface SceneDef {
  id: string;
  kind: SceneKind;
  /** Titre francais affichable. */
  title: string;
  /** Pour kind 'dialogue' : identifiant du fichier de dialogue a charger. */
  dialogueId?: string;
  /** Pour kind 'explore' uniquement (ADR 0013 §4) -- voir les quatre champs ci-dessous. */
  /** Carte a charger (`src/data/maps/<mapId>.ts`, cle du registre `src/data/maps/index.ts`). */
  mapId?: string;
  /**
   * Point d'apparition (cle de `MapDef.spawns`) utilise UNIQUEMENT a une
   * entree a froid sur la carte (nouvelle partie, reprise de sauvegarde, saut
   * `?scene=`) -- jamais entre deux etapes d'exploration qui se suivent sur
   * la MEME carte : Franklyn reste ou le dialogue l'a laisse (08-EXPLORATION.md
   * "Le groupe", contrat du lot 3.6b).
   */
  spawn?: string;
  /**
   * Suiveurs de cette etape (ADR 0024 §3), dans l'ordre d'affichage. Absent : la regle du
   * chapitre 1 s'applique (`exploreFollowerIds`, les coequipiers du tirage). Le chapitre 2
   * declare la sienne explicitement (l'enfant rejoint le groupe a un moment precis de
   * l'histoire, pas au tirage) -- voir `ChapterApp.enterExploreScene`.
   */
  followers?: FollowerId[];
  /**
   * Valeur posee dans `RunState.flags[etapeFlag]` a l'entree de la scene
   * (voir `withEtape`) : c'est elle qui fait apparaitre/disparaitre les
   * entites de la carte (`condition` de `src/data/maps/holt.ts`).
   *
   * Generalise en simple `string` par l'ADR 0021 (etait `Ch1Etape`) : chaque
   * chapitre exporte sa propre union de valeurs (`Ch1Etape` pour le premier),
   * mais le champ lui-meme ne connait plus qu'une chaine -- c'est
   * `ChapterDef.etapeFlag` qui dit dans QUEL drapeau elle s'ecrit.
   */
  etape?: string;
  /** Objectif principal (+ facultatifs) affiche pendant l'etape (`ObjectiveHud`). */
  objective?: ObjectiveDef;
  /** Scene sautee si la condition est fausse. */
  when?: Condition;
  /**
   * Numero de scene dans le chapitre (ADR 0021) : plusieurs `SceneDef` peuvent
   * partager le meme numero (ex. les scenes 4, 5 et 9 du chapitre 2, qui se
   * declinent en une etape d'exploration et son dialogue). Absent pour le
   * chapitre 1. Il n'est plus affiche depuis le lot 5.17 (plus de carte de titre
   * entre deux scenes) : il reste l'ordre de reference des gardes de contenu
   * (`tests/unit/ch2Content.test.ts`).
   */
  number?: number;
}

/**
 * Valeurs du drapeau `ch1.etape` (chapitre 1 uniquement -- un futur chapitre
 * aurait son propre drapeau). Source de verite unique : `src/data/maps/holt.ts`
 * et `src/data/maps/centre-examen.ts` ne font que LIRE ce type via `condition`
 * sur leurs entites -- les six premieres valeurs pour l'academie HOLT, les six
 * suivantes pour le centre d'examen (docs/design/09-MAPS-CHAPTER-1.md).
 */
export type Ch1Etape =
  | 'reveil'
  | 'discours'
  | 'examen'
  | 'tirage'
  | 'temps-libre'
  | 'depart'
  | 'arrivee'
  | 'hall'
  | 'salle1'
  | 'salle2'
  | 'salle3'
  | 'cour';

/** Nom du drapeau de `RunState.flags` qui porte l'etape courante du chapitre 1. */
export const CH1_ETAPE_FLAG = 'ch1.etape';

/**
 * Pose `scene.etape` dans le contexte a l'entree d'une scene `explore` (ADR
 * 0013 §4, contrat du lot 3.6b : "posé... à l'entrée de la scène"), sous le
 * drapeau `etapeFlag` (`ChapterDef.etapeFlag`, ADR 0021 -- repli sur
 * `CH1_ETAPE_FLAG` si omis, pour ne rien casser au chapitre 1). Pure, sans
 * effet sur une scene d'un autre type (`scene.etape` est alors absent).
 */
export function withEtape(ctx: NarrativeContext, scene: SceneDef, etapeFlag: string = CH1_ETAPE_FLAG): NarrativeContext {
  if (!scene.etape) return ctx;
  return { ...ctx, run: setFlag(ctx.run, etapeFlag, scene.etape) };
}

export class SceneRouter {
  private readonly scenes: SceneDef[];
  private ctx: NarrativeContext;
  private index: number;
  /** Drapeau d'etape de CE chapitre (ADR 0021) -- voir `withEtape`. */
  readonly etapeFlag: string;

  constructor(scenes: SceneDef[], ctx: NarrativeContext, etapeFlag: string = CH1_ETAPE_FLAG) {
    this.scenes = scenes;
    this.ctx = ctx;
    this.etapeFlag = etapeFlag;
    this.index = this.nextEligibleIndex(0);
    this.applyCurrentScene();
  }

  get context(): NarrativeContext {
    return this.ctx;
  }

  get finished(): boolean {
    return this.index >= this.scenes.length;
  }

  current(): SceneDef {
    const scene = this.scenes[this.index];
    if (!scene) throw new Error('SceneRouter : aucune scene courante, le chapitre est termine.');
    return scene;
  }

  /** Passe a la scene suivante ; renvoie null a la fin du chapitre. */
  next(): SceneDef | null {
    this.index = this.nextEligibleIndex(this.index + 1);
    if (this.index >= this.scenes.length) return null;
    this.applyCurrentScene();
    return this.current();
  }

  /**
   * Deux `SceneDef` jumelles partagent un identifiant, gardées par des `when` exclusifs (la fuite
   * et les conduits selon `ch2.porteur`) : `goTo` retient la jumelle ÉLIGIBLE, et seulement à
   * défaut la première (départ direct par `?scene=`, drapeau pas encore posé). Défaut de QA du
   * lot 5.11 : `goTo` prenait toujours la première, et `ChapterApp.advanceRouter` (qui repart de
   * `goTo(run.sceneId)`) faisait d'un `next()` depuis la jumelle « John »... la jumelle
   * « Abigail » elle-même -- avec Abigail pour porteuse, la grille relançait la fuite au lieu
   * d'ouvrir `ch2.grille`.
   */
  goTo(sceneId: string): void {
    let first = -1;
    for (let i = 0; i < this.scenes.length; i++) {
      const scene = this.scenes[i];
      if (!scene || scene.id !== sceneId) continue;
      if (first < 0) first = i;
      if (!scene.when || evaluateCondition(scene.when, this.ctx)) {
        first = i;
        break;
      }
    }
    if (first < 0) return;
    this.index = first;
    this.applyCurrentScene();
  }

  /**
   * Scène qui suivrait un `next()`, SANS avancer le routeur (lecture seule, aucun effet sur
   * `index`/`ctx`) -- lot 3.7b : sert à `ChapterApp.completeExploreScene` pour savoir, avant de
   * réellement changer de scène, si la transition mène au mode tactique (tampon "CONTACT" +
   * coupure, 08-EXPLORATION.md "Passer au combat") ou à une scène ordinaire.
   */
  peekNext(): SceneDef | null {
    const idx = this.nextEligibleIndex(this.index + 1);
    return idx < this.scenes.length ? (this.scenes[idx] ?? null) : null;
  }

  private nextEligibleIndex(from: number): number {
    let i = from;
    while (i < this.scenes.length) {
      const scene = this.scenes[i];
      if (scene && (!scene.when || evaluateCondition(scene.when, this.ctx))) return i;
      i++;
    }
    return this.scenes.length;
  }

  private applyCurrentScene(): void {
    const scene = this.scenes[this.index];
    if (!scene) return;
    this.ctx = { ...this.ctx, run: { ...this.ctx.run, sceneId: scene.id } };
  }
}

/** Scene du tirage (ADR 0014) : point de repere partage par `chapter.ts` et les helpers ci-dessous. */
export const TIRAGE_SCENE_ID = 'ch1.tirage';

/** Position de `sceneId` dans `CHAPTER_1_SCENES`, ou `-1` si absent. */
function sceneIndex(sceneId: string): number {
  return CHAPTER_1_SCENES.findIndex((s) => s.id === sceneId);
}

/**
 * Coequipiers qui suivent Franklyn en exploration (08-EXPLORATION.md "Le
 * groupe", ADR 0014 §5) : personne avant le tirage ("il est seul"), les deux
 * coequipiers de `RunState.roster.blue` apres. Pure -- ne lit que le
 * `RunState`, jamais le DOM ni `ExploreState` : c'est `chapter.ts` qui
 * repercute le resultat sur `ExploreState.setFollowers`.
 */
export function exploreFollowerIds(run: RunState): CharacterId[] {
  const tirageIdx = sceneIndex(TIRAGE_SCENE_ID);
  const currentIdx = sceneIndex(run.sceneId);
  const afterDraft = tirageIdx >= 0 && currentIdx > tirageIdx;
  if (!afterDraft) return [];
  return run.roster.blue.filter((id) => id !== 'franklyn');
}

/**
 * Les scenes du chapitre 1, dans l'ordre de docs/design/03-CHAPTER-1.md.
 * Le parcours interieur (scene 7) est deplie en plusieurs entrees pour porter
 * chacune son dialogueId ; les etapes d'exploration (ADR 0013 §4, epic 3 lot
 * 3.6b) portent la carte, le spawn a froid, l'etape et l'objectif -- voir
 * `SceneDef`. Les identifiants existants sont conserves (sauvegardes,
 * `?scene=`, tests e2e en dependent), y compris `ch1.hub` : c'etait une scene
 * `hub` (liste des cadets, `HubView`), c'est desormais une scene `explore`
 * (les cinq cadets abordes sur la carte) -- meme id, autre mecanisme.
 */
export const CHAPTER_1_SCENES: SceneDef[] = [
  { id: 'ch1.intro', kind: 'dialogue', title: 'Réveil', dialogueId: 'ch1.intro' },
  {
    id: 'ch1.vers-cantine',
    kind: 'explore',
    title: 'Rejoindre la cantine',
    mapId: 'holt',
    spawn: 'lit-franklyn',
    etape: 'reveil',
    objective: {
      id: 'ch1.vers-cantine',
      title: 'Rejoindre la cantine',
      context: "Le directeur s'adresse à la promotion.",
      // L'entite qui termine l'objectif porte le dialogueId de la scene SUIVANTE
      // (ch1.discours) -- regle unique du contrat du lot 3.6b, verifiee par
      // tests/unit/exploreScenes.test.ts.
      completionTrigger: 'cantine.place-franklyn',
      tasks: [
        {
          id: 'ch1.vers-cantine.parler',
          label: 'parler aux cadets',
          // Figurants du dortoir et des couloirs (docs/design/03/09-*.md) --
          // identifiants exacts de src/data/maps/holt.ts.
          entityIds: ['dortoir.figurant-1', 'dortoir.figurant-2', 'couloir.figurant-1', 'couloir.figurant-2'],
        },
      ],
    },
  },
  { id: 'ch1.discours', kind: 'dialogue', title: 'Le discours du directeur', dialogueId: 'ch1.discours' },
  {
    id: 'ch1.vers-examen',
    kind: 'explore',
    title: "Rejoindre les salles d'entraînement",
    mapId: 'holt',
    // Cold start uniquement (voir `SceneDef.spawn`) : la position reelle apres
    // ch1.discours est celle laissee par le dialogue (la place a la cantine).
    spawn: 'cantine',
    etape: 'examen',
    objective: {
      id: 'ch1.vers-examen',
      title: "Rejoindre les salles d'entraînement",
      context: "L'examen écrit commence.",
      // Porte le dialogueId de la scene suivante (ch1.exam).
      completionTrigger: 'entrainement.pupitre-franklyn',
    },
  },
  { id: 'ch1.exam', kind: 'dialogue', title: "L'examen écrit", dialogueId: 'ch1.exam' },
  { id: TIRAGE_SCENE_ID, kind: 'dialogue', title: 'Le tirage des équipes', dialogueId: 'ch1.tirage' },
  {
    id: 'ch1.hub',
    kind: 'explore',
    title: 'Avant le départ',
    mapId: 'holt',
    spawn: 'temps-libre',
    etape: 'temps-libre',
    objective: {
      id: 'ch1.hub',
      title: 'Rejoindre le garage',
      context: "Le fourgon part dès que l'équipe est prête.",
      // Porte le dialogueId de la scene suivante (ch1.fourgon).
      completionTrigger: 'garage.fourgon',
      tasks: [
        {
          id: 'ch1.hub.parler',
          label: 'parler aux cadets',
          // Les cinq cadets (docs/design/09-MAPS-CHAPTER-1.md "Placement des
          // cadets au temps libre") -- identifiants exacts de holt.ts. Chaque
          // entite porte son propre dialogueId (ch1.hub.<cadet>, inchange) :
          // une conversation annexe, elle n'avance pas le routeur.
          entityIds: [
            'infirmerie.abigail',
            'armurerie.john',
            'archives.letitia',
            'cour.grover',
            'entrainement.zachary',
          ],
        },
      ],
    },
  },
  { id: 'ch1.fourgon', kind: 'dialogue', title: 'Le trajet en fourgon', dialogueId: 'ch1.fourgon' },
  /*
   * Le centre d'examen (scène 7, epic 3 lot 3.7b) : docs/design/09-MAPS-CHAPTER-1.md
   * "Le centre d'examen désaffecté" / "Les salles deviennent des lieux". Cinq étapes
   * d'exploration sur `centre-examen` (`src/data/maps/centre-examen.ts`), qui posent
   * `ch1.etape` = 'hall' | 'salle1' | 'salle2' | 'salle3' | 'cour' (`Ch1Etape`).
   *
   * `ch1.salle1`/`ch1.salle2`/`ch1.salle3` GARDENT leurs identifiants (sauvegardes,
   * `?scene=`, tests en dépendent) mais deviennent des scènes `explore` -- même
   * mécanisme que `ch1.hub` au lot 3.6b (liste de cadets -> carte, même id, autre
   * type de scène). Le dialogue qu'elles chargeaient (fichier JSON inchangé, texte et
   * jets inchangés) est désormais ouvert PAR UNE ENTITÉ de la pièce, à un nœud choisi
   * (`dialogueId`/`startNode` de `centre-examen.ts`) : celle qui termine l'objectif de
   * la pièce joue le dialogue en entier et fait avancer le routeur elle-même (règle du
   * lot 3.6b étendue par le lot 3.7b, voir `ChapterApp.handleExploreInteraction` --
   * jusqu'ici seul le dialogueId de la scène SUIVANTE était montré, ce lot introduit le
   * cas où le déclencheur porte lui-même un nœud non par défaut) ; toute autre entité à
   * dialogue ouvre une conversation annexe qui n'avance pas le routeur (inchangé).
   *
   * `ch1.centre-hall` (arrivée + briefing) et `ch1.cour` (portail -> combat) sont de
   * NOUVELLES scènes, sans équivalent avant ce lot.
   */
  {
    id: 'ch1.centre-hall',
    kind: 'explore',
    title: "Le centre d'examen désaffecté",
    mapId: 'centre-examen',
    spawn: 'parking',
    etape: 'hall',
    objective: {
      id: 'ch1.centre-hall',
      title: "Rejoindre l'instructeur",
      context: 'Le centre désaffecté attend, béton taggé et néons morts.',
      // hall.instructeur joue le briefing (ch1.centre-hall.json) puis avance -- et déverrouille
      // hall.porte-nord au passage (voir centre-examen.ts) : sans lui parler, la porte vers la
      // salle 1 reste fermée, le briefing est donc bloquant.
      completionTrigger: 'hall.instructeur',
    },
  },
  {
    id: 'ch1.salle1',
    kind: 'explore',
    title: 'Salle 1 — La porte et le chien',
    mapId: 'centre-examen',
    // Cold start uniquement (voir `SceneDef.spawn`) : en jeu normal, Franklyn reste où le
    // hall l'a laissé, tout près de la porte (spawn au cas où, ex. `?scene=ch1.salle1`).
    spawn: 'salle1',
    etape: 'salle1',
    objective: {
      id: 'ch1.salle1',
      title: 'Franchir la salle 1',
      context: 'La porte résiste, verrouillée par un panneau électronique.',
      // C'est la SORTIE (salle1.porte-nord, nœud "sortie") qui termine l'étape -- pas le premier
      // objet touché : le panneau d'entrée (salle1.panneau-porte) et le chien/l'otage sont des
      // beats facultatifs, joués séparément (voir centre-examen.ts), qu'on peut ignorer sans
      // bloquer la progression.
      completionTrigger: 'salle1.porte-nord',
    },
  },
  {
    id: 'ch1.salle2',
    kind: 'explore',
    title: 'Salle 2 — Le choix coûteux',
    mapId: 'centre-examen',
    spawn: 'salle2',
    etape: 'salle2',
    objective: {
      id: 'ch1.salle2',
      title: 'Franchir la salle 2',
      context: 'Une armoire sécurisée, une porte verrouillée en face.',
      completionTrigger: 'salle2.porte-nord',
      tasks: [
        {
          id: 'ch1.salle2.armoire',
          label: "examiner l'armoire pour récupérer un second taser",
          entityIds: ['salle2.armoire'],
        },
      ],
    },
  },
  {
    id: 'ch1.salle3',
    kind: 'explore',
    title: 'Salle 3 — Le gaz et la video',
    mapId: 'centre-examen',
    spawn: 'salle3',
    etape: 'salle3',
    objective: {
      id: 'ch1.salle3',
      title: 'Franchir la salle 3',
      context: 'Un gaz irritant commence à envahir la pièce.',
      completionTrigger: 'salle3.porte-nord',
    },
  },
  {
    id: 'ch1.cour',
    kind: 'explore',
    title: 'La cour de containers',
    mapId: 'centre-examen',
    spawn: 'cour',
    etape: 'cour',
    objective: {
      id: 'ch1.cour',
      title: 'Franchir le portail',
      context: "L'équipe adverse ne va plus tarder.",
      // Zone, pas de dialogue : tampon "CONTACT" + passage au mode tactique
      // (ChapterApp.completeExploreScene, "Passer au combat" de 08-EXPLORATION.md).
      completionTrigger: 'cour.portail',
    },
  },
  { id: 'ch1.affrontement', kind: 'tactical', title: "L'affrontement final" },
  { id: 'ch1.bal', kind: 'dialogue', title: 'Le bal de promo', dialogueId: 'ch1.bal' },
];
